import { useState, useEffect, useCallback } from 'react'
import { useFiltersStore } from '@/store/filters'
import { biApi } from '@/lib/api'
import {
  MousePointerClick,
  CheckCircle2,
  Percent,
  Globe,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  Calendar,
  Download,
  FileText,
  ChevronLeft,
  ChevronRight,
  Filter,
  Radio,
  Clock,
  BarChart3,
  TrendingUp as LineChartIcon,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  Line,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LabelList,
} from 'recharts'
import clsx from 'clsx'

interface ReportData {
  meta: {
    timezone: string
    startDate: string
    endDate: string
    granularity: string
    totalDays: number
    comparisonPeriod: { start: string; end: string }
    lastUpdated: string
    filtersApplied: Record<string, any>
    isAdmin: boolean
  }
  summary: {
    clicks: { total: number; previous: number; trend: number; trendDirection: string }
    conversions: { total: number; previous: number; trend: number; trendDirection: string }
    conversionRate: { value: number; previous: number; trend: number; trendDirection: string }
    uniqueCountries: number
  }
  timeSeries: Array<{
    date: string
    clicks: number
    conversions: number
    rate: number
  }>
  byCountry: Array<{
    country: string
    countryName: string
    flag: string
    clicks: number
    conversions: number
    rate: number
    percentage: number
  }>
  byOperator: Array<{
    operator: string
    country: string
    clicks: number
    conversions: number
    rate: number
  }>
  bySource: Array<{
    source: string
    clicks: number
    conversions: number
    rate: number
    percentage: number
  }>
  rawData: Array<{
    date: string
    country: string
    countryName: string
    operator: string
    source: string
    productName: string
    clicks: number
    conversions: number
    rate: number
    heClicks?: number
    wifiClicks?: number
    heRate?: number
  }>
}

interface FilterOptions {
  countries: Array<{ code: string; name: string; flag: string }>
  operators: { list: Array<{ operator: string; country: string; label: string }>; byCountry: Record<string, string[]> }
  products: Array<{ id: number; name: string; country: string; operator: string; source: string | null }>
  sources: string[]
  timezones: Array<{ value: string; label: string }>
}

// Date presets
const DATE_PRESETS = [
  { label: 'Hoy', days: 0 },
  { label: '7 días', days: 7 },
  { label: '30 días', days: 30 },
  { label: '90 días', days: 90 },
]

// KPI Card component
function KPICard({
  title,
  value,
  previousValue,
  icon: Icon,
  trend,
  trendDirection,
  format = 'number',
  color = 'primary',
}: {
  title: string
  value: number
  previousValue?: number
  icon: React.FC<{ className?: string }>
  trend?: number
  trendDirection?: string
  format?: 'number' | 'percent'
  color?: 'primary' | 'success' | 'warning'
}) {
  const formatValue = (val: number) => {
    if (format === 'percent') {
      return `${val.toFixed(2)}%`
    }
    if (val >= 1000000) {
      return `${(val / 1000000).toFixed(2)}M`
    }
    if (val >= 1000) {
      return `${(val / 1000).toFixed(1)}K`
    }
    return val.toLocaleString()
  }
  
  const colorClasses = {
    primary: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600',
    success: 'bg-green-50 dark:bg-green-500/20 text-green-600',
    warning: 'bg-yellow-50 dark:bg-yellow-500/20 text-yellow-600',
  }
  
  return (
    <div className="kpi-card">
      <div className="flex items-start justify-between">
        <div className={clsx('p-2 rounded-lg', colorClasses[color])}>
          <Icon className="w-5 h-5" />
        </div>
        {trend !== undefined && (
          <div className={clsx(
            'flex items-center gap-1 text-sm font-medium',
            trendDirection === 'up' && 'text-green-500',
            trendDirection === 'down' && 'text-red-500',
            trendDirection === 'stable' && 'text-gray-400'
          )}>
            {trendDirection === 'up' && <TrendingUp className="w-4 h-4" />}
            {trendDirection === 'down' && <TrendingDown className="w-4 h-4" />}
            {trendDirection === 'stable' && <Minus className="w-4 h-4" />}
            <span>{trend > 0 ? '+' : ''}{trend}%</span>
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{formatValue(value)}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{title}</p>
        {previousValue !== undefined && (
          <p className="text-xs text-gray-400 mt-1">
            Anterior: {formatValue(previousValue)}
          </p>
        )}
      </div>
    </div>
  )
}

export default function Reports() {
  const { 
    filters, 
    options: globalOptions,
    setFilter,
    setDateRange,
    setGranularity: setGranularityStore
  } = useFiltersStore()
  
  // Destructure from store
  const {
    startDate,
    endDate,
    granularity,
    timezone: selectedTimezone,
    countries: selectedCountries,
    operators: selectedOperators,
    sources: selectedSources,
    flow: selectedFlow
  } = filters
  
  // Data state
  const [data, setData] = useState<ReportData | null>(null)
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Table pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [chartType, setChartType] = useState<'bar' | 'line'>('bar')
  const rowsPerPage = 15

  // Load filter options
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const response = await biApi.getFilterOptions()
        if (response.data.success) {
          setFilterOptions(response.data.data)
        }
      } catch (err) {
        console.error('Error loading filters:', err)
      }
    }
    loadFilters()
  }, [])

  // Apply date preset
  const applyPreset = (days: number) => {
    const end = new Date()
    const start = new Date()
    if (days > 0) {
      start.setDate(start.getDate() - days)
    }
    setDateRange(start.toISOString().split('T')[0], end.toISOString().split('T')[0])
  }

  // Fetch report data
  const fetchReport = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Block hourly queries for ranges > 3 days
      const rangeDays = Math.ceil(
        (new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)
      ) + 1
      const effectiveGranularity = (granularity === 'hour' && rangeDays > 7) ? 'day' : granularity
      
      const response = await biApi.getHistoricalReport({
        startDate,
        endDate,
        granularity: effectiveGranularity,
        timezone: selectedTimezone,
        countries: selectedCountries.length > 0 ? selectedCountries : undefined,
        operators: selectedOperators.length > 0 ? selectedOperators : undefined,
        sources: selectedSources.length > 0 ? selectedSources : undefined,
        flow: selectedFlow || undefined,
      })
      
      if (response.data.success) {
        setData(response.data.data)
        setCurrentPage(1) // Reset pagination
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar el reporte')
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, granularity, selectedTimezone, selectedCountries, selectedOperators, selectedSources, selectedFlow])

  // Export to CSV
  const handleExport = async () => {
    try {
      setExporting(true)
      
      const response = await biApi.exportReport({
        startDate,
        endDate,
        format: 'csv',
        timezone: selectedTimezone,
        countries: selectedCountries.length > 0 ? selectedCountries : undefined,
        operators: selectedOperators.length > 0 ? selectedOperators : undefined,
        sources: selectedSources.length > 0 ? selectedSources : undefined,
      })
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `reporte_${startDate}_${endDate}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      setError('Error al exportar el reporte')
    } finally {
      setExporting(false)
    }
  }

  // Paginated raw data
  const paginatedData = data?.rawData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  ) || []
  
  const totalPages = Math.ceil((data?.rawData.length || 0) / rowsPerPage)

  // Calculate date range in days for hourly limit
  const daysDiff = Math.ceil(
    (new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)
  ) + 1
  const hourlyExceedsLimit = granularity === 'hour' && daysDiff > 7

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Reportes Históricos
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Analiza métricas por rango de fechas personalizado
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={!data || exporting}
            className="btn-secondary btn-sm flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exportando...' : 'Exportar CSV'}
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-orange-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Filtros</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Fecha Inicio
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setDateRange(e.target.value, endDate)}
              className="input w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Fecha Fin
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setDateRange(startDate, e.target.value)}
              className="input w-full"
            />
          </div>
          
          {/* Granularity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Granularidad
            </label>
            <select
              value={granularity}
              onChange={(e) => setGranularityStore(e.target.value as any)}
              className="input w-full"
            >
              <option value="hour">Por hora</option>
              <option value="day">Por día</option>
              <option value="week">Por semana</option>
            </select>
          </div>
          
          {/* Timezone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Clock className="w-4 h-4 inline mr-1" />
              Zona Horaria
            </label>
            <select
              value={selectedTimezone}
              onChange={(e) => setFilter('timezone', e.target.value)}
              className="input w-full"
            >
              {globalOptions.timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Country Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              País
            </label>
            <select
              value={selectedCountries[0] || ''}
              onChange={(e) => setFilter('countries', e.target.value ? [e.target.value] : [])}
              className="input w-full"
            >
              <option value="">Todos los países</option>
              {filterOptions?.countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Operator Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Operador
            </label>
            <select
              value={selectedOperators[0] || ''}
              onChange={(e) => setFilter('operators', e.target.value ? [e.target.value] : [])}
              className="input w-full"
            >
              <option value="">Todos los operadores</option>
              {filterOptions?.operators.list.map((o) => (
                <option key={`${o.operator}-${o.country}`} value={o.operator}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Source Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Fuente de Tráfico
            </label>
            <select
              value={selectedSources[0] || ''}
              onChange={(e) => setFilter('sources', e.target.value ? [e.target.value] : [])}
              className="input w-full"
            >
              <option value="">Todas las fuentes</option>
              {filterOptions?.sources.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          
          {/* Flow Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Flujo
            </label>
            <select
              value={selectedFlow}
              onChange={(e) => setFilter('flow', e.target.value)}
              className="input w-full"
            >
              <option value="">Todos los flujos</option>
              <option value="sms">SMS</option>
              <option value="sat">SAT</option>
            </select>
          </div>
        </div>
        
        {/* Date Presets & Generate Button */}
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <span className="text-sm text-gray-500 mr-2">Presets:</span>
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.days}
              onClick={() => applyPreset(preset.days)}
              className="px-3 py-1 text-sm rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-orange-100 dark:hover:bg-orange-900/30 text-gray-700 dark:text-gray-300 hover:text-orange-600 transition-colors"
            >
              {preset.label}
            </button>
          ))}
          
          <div className="flex-1" />
          
          <button
            onClick={fetchReport}
            disabled={loading}
            className="btn-primary flex items-center gap-2"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            {loading ? 'Generando...' : 'Generar Reporte'}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Results Section */}
      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              title="Total Clicks"
              value={data.summary.clicks.total}
              previousValue={data.summary.clicks.previous}
              icon={MousePointerClick}
              trend={data.summary.clicks.trend}
              trendDirection={data.summary.clicks.trendDirection}
              color="primary"
            />
            <KPICard
              title="Conversiones"
              value={data.summary.conversions.total}
              previousValue={data.summary.conversions.previous}
              icon={CheckCircle2}
              trend={data.summary.conversions.trend}
              trendDirection={data.summary.conversions.trendDirection}
              color="success"
            />
            <KPICard
              title="Tasa de Conversión"
              value={data.summary.conversionRate.value}
              previousValue={data.summary.conversionRate.previous}
              icon={Percent}
              trend={data.summary.conversionRate.trend}
              trendDirection={data.summary.conversionRate.trendDirection}
              format="percent"
              color="warning"
            />
            <KPICard
              title="Países Únicos"
              value={data.summary.uniqueCountries}
              icon={Globe}
              color="primary"
            />
          </div>

          {/* Time Series Chart */}
          <div className="card p-4">
            {hourlyExceedsLimit && (
              <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-center gap-2">
                <Clock className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                <p className="text-sm text-yellow-700 dark:text-yellow-400">
                  La vista por hora solo está disponible para rangos de hasta 7 días. Se muestra la vista diaria. Reduce el rango de fechas para ver datos por hora.
                </p>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Tendencia de {granularity === 'hour' ? 'Horas' : granularity === 'day' ? 'Días' : 'Semanas'}
              </h3>
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setChartType('bar')}
                  className={clsx(
                    'p-1.5 rounded-md transition-colors',
                    chartType === 'bar' ? 'bg-white dark:bg-gray-700 shadow-sm text-orange-600' : 'text-gray-400 hover:text-gray-600'
                  )}
                  title="Barras"
                >
                  <BarChart3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setChartType('line')}
                  className={clsx(
                    'p-1.5 rounded-md transition-colors',
                    chartType === 'line' ? 'bg-white dark:bg-gray-700 shadow-sm text-orange-600' : 'text-gray-400 hover:text-gray-600'
                  )}
                  title="Líneas"
                >
                  <LineChartIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.timeSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    tickFormatter={(value) => {
                      return value.split('-').slice(1).join('/')
                    }}
                  />
                  <YAxis 
                    yAxisId="left"
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    label={{ value: 'Clicks', angle: -90, position: 'insideLeft', fill: '#f97316', fontSize: 12 }}
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    label={{ value: 'Conversiones', angle: 90, position: 'insideRight', fill: '#22c55e', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    formatter={(value: number, name: string) => {
                      if (name === 'clicks') return [value.toLocaleString(), 'Clicks']
                      if (name === 'conversions') return [value.toLocaleString(), 'Conversiones']
                      return [`${value.toFixed(2)}%`, 'Tasa']
                    }}
                    labelFormatter={(label: string) => {
                      return label
                    }}
                    itemSorter={() => 0}
                  />
                  <Legend />
                  {chartType === 'bar' ? (
                    <Bar
                      yAxisId="left"
                      dataKey="clicks"
                      name="Clicks"
                      fill="#f97316"
                      radius={[4, 4, 0, 0]}
                      opacity={0.85}
                    />
                  ) : (
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="clicks"
                      stroke="#f97316"
                      strokeWidth={3}
                      dot={false}
                      name="Clicks"
                    />
                  )}
                  {chartType === 'bar' ? (
                    <Bar
                      yAxisId="right"
                      dataKey="conversions"
                      name="Conversiones"
                      fill="#22c55e"
                      radius={[4, 4, 0, 0]}
                      opacity={0.85}
                    >
                      <LabelList
                        dataKey="rate"
                        position="top"
                        formatter={(value: number) => `${value.toFixed(2)}%`}
                        style={{
                          fill: '#fbbf24',
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                        offset={8}
                      />
                    </Bar>
                  ) : (
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="conversions"
                      stroke="#22c55e"
                      strokeWidth={2}
                      dot={false}
                      name="Conversiones"
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* By Country */}
            <div className="card p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                Distribución por País
              </h3>
              {data.byCountry.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <defs>
                        <linearGradient id="pieGradient0" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#ff9500" stopOpacity={1}/>
                          <stop offset="100%" stopColor="#f97316" stopOpacity={1}/>
                        </linearGradient>
                        <linearGradient id="pieGradient1" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#fbbf24" stopOpacity={1}/>
                          <stop offset="100%" stopColor="#fb923c" stopOpacity={1}/>
                        </linearGradient>
                        <linearGradient id="pieGradient2" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#f97316" stopOpacity={1}/>
                          <stop offset="100%" stopColor="#ea580c" stopOpacity={1}/>
                        </linearGradient>
                        <linearGradient id="pieGradient3" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#fdba74" stopOpacity={1}/>
                          <stop offset="100%" stopColor="#fb923c" stopOpacity={1}/>
                        </linearGradient>
                        <linearGradient id="pieGradient4" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#ea580c" stopOpacity={1}/>
                          <stop offset="100%" stopColor="#c2410c" stopOpacity={1}/>
                        </linearGradient>
                      </defs>
                      <Pie
                        data={data.byCountry}
                        dataKey="clicks"
                        nameKey="countryName"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        label={({ countryName, percentage }) => `${countryName} (${percentage}%)`}
                        labelLine={{ stroke: '#9ca3af' }}
                      >
                        {data.byCountry.map((entry, index) => (
                          <Cell 
                            key={entry.country} 
                            fill={`url(#pieGradient${index % 5})`}
                            style={{ filter: 'drop-shadow(0 2px 4px rgba(249, 115, 22, 0.3))' }}
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#1f2937',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                        formatter={(value: number) => [value.toLocaleString(), 'Clicks']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400">
                  Sin datos disponibles
                </div>
              )}
            </div>

            {/* By Source */}
            <div className="card p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                Distribución por Fuente
              </h3>
              {data.bySource.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.bySource} layout="vertical">
                      <defs>
                        <linearGradient id="barGradient" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#ff9500" stopOpacity={1}/>
                          <stop offset="50%" stopColor="#f97316" stopOpacity={1}/>
                          <stop offset="100%" stopColor="#ea580c" stopOpacity={1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                      <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                      <YAxis 
                        dataKey="source" 
                        type="category" 
                        tick={{ fill: '#9ca3af', fontSize: 12 }} 
                        width={100}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#1f2937',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                        formatter={(value: number) => [value.toLocaleString(), 'Clicks']}
                      />
                      <Bar 
                        dataKey="clicks" 
                        fill="url(#barGradient)" 
                        radius={[0, 4, 4, 0]}
                        style={{ filter: 'drop-shadow(0 2px 4px rgba(249, 115, 22, 0.4))' }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <Radio className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Sin fuentes asignadas</p>
                    <p className="text-xs mt-1">Las fuentes aparecerán cuando se complete el mapeo</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Data Table */}
          <div className="card overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Datos Detallados
              </h3>
              <span className="text-sm text-gray-500">
                {data.rawData.length} registros
              </span>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      País
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Operador
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fuente
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Campaña
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Clicks
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      HE
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      WIFI
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      HE %
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Conversiones
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tasa
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedData.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {row.date}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {row.countryName}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {row.operator}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {row.source}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {row.productName}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white tabular-nums">
                        {row.clicks.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-green-600 dark:text-green-400 tabular-nums">
                        {(row.heClicks ?? 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-400 dark:text-gray-500 tabular-nums">
                        {(row.wifiClicks ?? 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-right tabular-nums">
                        <span className={clsx(
                          (row.heRate ?? 0) >= 60 ? 'text-green-600 dark:text-green-400' : (row.heRate ?? 0) >= 30 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-500 dark:text-red-400'
                        )}>
                          {(row.heRate ?? 0).toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-green-600 dark:text-green-400 tabular-nums">
                        {row.conversions.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-orange-600 dark:text-orange-400 tabular-nums">
                        {row.rate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  Mostrando {(currentPage - 1) * rowsPerPage + 1} - {Math.min(currentPage * rowsPerPage, data.rawData.length)} de {data.rawData.length}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Página {currentPage} de {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Meta info */}
          <div className="text-center text-sm text-gray-400">
            Período: {data.meta.startDate} a {data.meta.endDate} ({data.meta.totalDays} días) | 
            Comparado con: {data.meta.comparisonPeriod.start} a {data.meta.comparisonPeriod.end} |
            Última actualización: {new Date(data.meta.lastUpdated).toLocaleString()}
          </div>
        </>
      )}

      {/* Empty State */}
      {!data && !loading && !error && (
        <div className="card p-12 text-center">
          <Calendar className="w-16 h-16 mx-auto text-orange-500 opacity-50 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Selecciona un rango de fechas
          </h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Configura los filtros deseados y haz clic en "Generar Reporte" para ver las métricas históricas.
          </p>
        </div>
      )}
    </div>
  )
}
